/**
 * GifPicker — web GIF search grid backed by the shared Giphy hook.
 * Mirrors the mobile picker's behaviour (trending by default, debounced search).
 */
import { useState } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import giphyService from '@wlb/shared/api/services/giphyService';
import { useGifSearch } from '@wlb/shared/hooks/useGifSearch';
import type { GifSelection, GiphyGif } from '@wlb/shared/types/gif';
import { CheckIcon, SearchIcon } from './icons';
import { Skeleton, SkeletonGroup, skeletonKeys } from './Skeleton';
import './gifPicker.css';

interface GifPickerProps {
  onSelect: (gif: GifSelection) => void;
  disabled?: boolean;
  /**
   * Adds a second line to the empty state. Used by the full-height New Post
   * sheet, where a lone "No GIFs found" leaves a large blank panel; the
   * compact composer panels keep the single line.
   */
  showEmptyHint?: boolean;
}

/** Fills three columns on desktop, two on the narrow sheet. */
const SKELETON_COUNT = 9;

export function GifPicker({ onSelect, disabled = false, showEmptyHint = false }: GifPickerProps) {
  const { t } = useTranslation();
  const { search, setSearch, gifs, loading, error, configured, retry } = useGifSearch();
  // Marks the tile that was just chosen. The picker closes right after, so this
  // is a confirmation of the click rather than a persistent selection.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleSelect = (gif: GiphyGif) => {
    setSelectedId(gif.id);
    onSelect(giphyService.toGifSelection(gif));
  };

  const renderGif = (gif: GiphyGif) => (
    <li className="gif-picker__cell" key={gif.id}>
      <button
        className={`gif-picker__item${selectedId === gif.id ? ' gif-picker__item--selected' : ''}`}
        type="button"
        disabled={disabled}
        aria-pressed={selectedId === gif.id}
        onClick={() => handleSelect(gif)}
        aria-label={t('gifPicker.accessibility.selectGif', {
          title: gif.title || t('gifPicker.accessibility.untitledGif'),
        })}
      >
        <img
          src={gif.url}
          alt=""
          aria-hidden
          loading="lazy"
          width={gif.width || undefined}
          height={gif.height || undefined}
        />
        <span className="gif-picker__check" aria-hidden="true">
          <CheckIcon size={14} />
        </span>
      </button>
    </li>
  );

  const renderBody = () => {
    if (!configured) {
      return <p className="gif-picker__status">{t('gifPicker.unavailable')}</p>;
    }
    if (loading) {
      return (
        <SkeletonGroup className="gif-picker__skeleton-grid" label={t('gifPicker.loading')}>
          {skeletonKeys(SKELETON_COUNT).map(key => (
            <Skeleton shape="rect" className="gif-picker__skeleton-cell" key={key} />
          ))}
        </SkeletonGroup>
      );
    }
    if (error) {
      return (
        <div className="gif-picker__status" role="alert">
          <p>{t('gifPicker.error')}</p>
          <button className="gif-picker__retry" type="button" onClick={retry}>
            {t('gifPicker.retry')}
          </button>
        </div>
      );
    }
    if (gifs.length === 0) {
      return (
        <div className="gif-picker__status">
          <p>{t('gifPicker.empty')}</p>
          {showEmptyHint && <p className="gif-picker__status-hint">{t('gifPicker.emptyHint')}</p>}
        </div>
      );
    }
    return <ul className="gif-picker__grid">{gifs.map(renderGif)}</ul>;
  };

  return (
    <div className="gif-picker" role="group" aria-label={t('gifPicker.accessibility.picker')}>
      {configured && (
        <div className="gif-picker__search">
          <SearchIcon size={16} className="gif-picker__search-icon" aria-hidden />
          <input
            className="gif-picker__search-input"
            type="search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder={t('gifPicker.searchPlaceholder')}
            aria-label={t('gifPicker.searchPlaceholder')}
          />
        </div>
      )}
      <div className="gif-picker__body" aria-busy={loading} aria-live="polite">
        {renderBody()}
      </div>
    </div>
  );
}
